import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Search, MessageSquare, ThumbsUp, Eye, 
  ChevronDown, ChevronUp, User, Clock, Tag, Loader2
} from "lucide-react";

const CATEGORIES = [
  { name: "All Topics", color: "bg-primary" },
  { name: "Visas & Immigration", color: "bg-blue-500" },
  { name: "Digital Nomads", color: "bg-purple-500" },
  { name: "Healthcare", color: "bg-red-500" },
  { name: "Real Estate", color: "bg-green-500" },
  { name: "Taxes & Finance", color: "bg-yellow-500" },
  { name: "Education", color: "bg-pink-500" },
  { name: "Cost of Living", color: "bg-orange-500" },
  { name: "Lifestyle", color: "bg-teal-500" },
  { name: "Safety", color: "bg-indigo-500" },
  { name: "Moving Logistics", color: "bg-cyan-500" }
];

const ForumPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Topics");
  const [expandedPost, setExpandedPost] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/forum/posts`);
        setPosts(response.data);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Failed to fetch posts:", error);
        }
        toast.error("We couldn't load discussions right now. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = posts.filter(post => {
    const matchesSearch = 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All Topics" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getCategoryColor = (categoryName) => {
    const cat = CATEGORIES.find(c => c.name === categoryName);
    return cat ? cat.color : "bg-gray-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2" data-testid="forum-title">
                Expat Community Forum
              </h1>
              <p className="text-muted-foreground">
                Real questions and answers from expats around the world
              </p>
            </div>
            <div className="text-sm text-muted-foreground bg-card px-4 py-2 rounded-lg border border-primary/10">
              <MessageSquare className="h-4 w-4 inline mr-2" />
              {posts.length} discussions
            </div>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search discussions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-card"
              data-testid="forum-search"
            />
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setSelectedCategory(cat.name)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat.name
                    ? `${cat.color} text-white`
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-primary/10">
              <MessageSquare className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
              <p className="text-muted-foreground">No discussions found matching your criteria</p>
            </div>
          ) : (
            filteredPosts.map((post) => (
              <div
                key={post.id}
                className="bg-card rounded-xl border border-primary/10 overflow-hidden card-hover"
              >
                {/* Post Header */}
                <div 
                  className="p-6 cursor-pointer"
                  onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                  data-testid={`post-${post.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${getCategoryColor(post.category)}`}>
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-primary mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground text-sm line-clamp-2">
                        {post.content}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {expandedPost === post.id ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>

                  {/* Post Meta */}
                  <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ThumbsUp className="h-4 w-4" />
                      <span>{post.upvotes}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      <span>{post.views}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      <span>{post.answers?.length || 0} answers</span>
                    </div>
                  </div>
                </div>

                {/* Expanded Content - Answers */}
                {expandedPost === post.id && post.answers && post.answers.length > 0 && (
                  <div className="border-t border-primary/10 bg-muted/30">
                    {post.answers.map((answer, idx) => (
                      <div 
                        key={answer.id || idx}
                        className="p-6 border-b border-primary/5 last:border-0"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                            <User className="h-5 w-5 text-accent" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-primary">{answer.author}</span>
                              <span className="text-sm text-muted-foreground">
                                {formatDate(answer.created_at)}
                              </span>
                              {idx === 0 && (
                                <span className="px-2 py-0.5 rounded-full bg-accent/20 text-accent text-xs font-medium">
                                  Top Answer
                                </span>
                              )}
                            </div>
                            <p className="text-foreground/80 whitespace-pre-line">
                              {answer.content}
                            </p>
                            <div className="flex items-center gap-2 mt-3">
                              <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground">
                                <ThumbsUp className="h-4 w-4" />
                                <span>{answer.upvotes}</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center bg-card rounded-xl border border-primary/10 p-8">
          <h3 className="text-xl font-semibold text-primary mb-2">Ready to Find Your Perfect Country?</h3>
          <p className="text-muted-foreground mb-4">
            Take our questionnaire and get personalized recommendations
          </p>
          <Button
            onClick={() => navigate("/questionnaire")}
            className="btn-animated bg-primary"
            data-testid="cta-questionnaire"
          >
            Start Questionnaire
          </Button>
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-center text-sm text-muted-foreground">
          The information shared in this forum is from community members and should not be considered official advice. 
          Always verify important information with official sources.
        </p>
      </div>
    </div>
  );
};

export default ForumPage;
